import * as React from 'react'
import { useState } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'

import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import type { FormErrors } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateHiringPhase } from 'modules/ats/hooks/hiringStages/useCreateHiringPhase'
import { HiringStage } from 'modules/ats/hooks/hiringStages/useHiringStages'

interface NewPhaseFormValues {
  name: string
  atsHiringStageId: number
  position: number
}

interface Props {
  onClose: () => void
  currentStage: HiringStage
  editableStages: HiringStage[]
}

export const AddPhaseToStageModal = ({ onClose, currentStage, editableStages }: Props) => {
  const { create } = useCreateHiringPhase()
  const [selectedStage, setSelectedStage] = useState<HiringStage>(currentStage)

  const newPhasePosition = () => {
    return (
      selectedStage.hiringPhases?.reduce((max, phase) => {
        return phase.position > max ? phase.position : max
      }, 0) + 1
    )
  }

  const form = useForm<NewPhaseFormValues>({
    defaultValues: {
      name: '',
      atsHiringStageId: currentStage.id,
      position: newPhasePosition(),
    },
    validate: (data) => {
      const errors: FormErrors<NewPhaseFormValues> = {}

      if (!data.atsHiringStageId) {
        errors.atsHiringStageId = i18n.t('common.empty')
      }

      if (!data.name) {
        errors.name = i18n.t(
          'ats.hiring_pipelines.settings.sidepanel.new_phase_modal.phase_input_error'
        )
      }

      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await create({
        name: data.name,
        atsHiringStageId: data.atsHiringStageId,
        position: newPhasePosition(),
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        onClose()
      }

      return handled
    },
  })

  const stageOptions = editableStages.map((stage) => {
    return { label: stage.label, value: stage.id }
  })

  const onChangeStage = (value: string) => {
    form.setValues({ ...form.values, atsHiringStageId: Number(value) })
    const stage = editableStages.find((stage) => stage.id === Number(value))
    setSelectedStage(stage as HiringStage)
  }

  return (
    <Modal
      title={i18n.t('ats.hiring_pipelines.settings.sidepanel.new_phase_modal.title')}
      description={i18n.t('ats.hiring_pipelines.settings.sidepanel.new_phase_modal.description')}
      illustration="automations"
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={() => onClose()}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="negative" submit>
            {i18n.t('common.save')}
          </Button>
        </Box>
      }
    >
      <Box gap="s24">
        <Select
          none={i18n.t('common.select')}
          label={i18n.t('ats.hiring_pipelines.settings.sidepanel.new_phase_modal.stage_label')}
          options={stageOptions}
          value={String(form.values.atsHiringStageId)}
          error={form.touched.atsHiringStageId ? form.errors.atsHiringStageId : undefined}
          onChange={(value) => onChangeStage(value)}
          mandatory
        />
        <TextField
          label={i18n.t('ats.hiring_pipelines.settings.sidepanel.new_phase_modal.phase_label')}
          value={form.values.name}
          onChange={(value) => form.setValues({ ...form.values, name: value })}
          error={form.touched.name ? form.errors.name : undefined}
          mandatory
        />
      </Box>
    </Modal>
  )
}
